import * as XLSX from "xlsx";
import FileSaver from "file-saver";

import { useToast } from "../../../../hooks/toast";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";

import { convertFileToFormData, formatFileSize } from "../../utils";
import {
  defaultersErrorsData,
  defaultersResetErrorsData,
  getDefaultersData,
} from "../../../../redux/modules/defaulters/actions";
import api from "../../../../service";

import {
  DnDUploader,
  ModalButtonsWrapper,
  ModalTitle,
  UploaderContainer,
  FlexContainer,
  Warning,
} from "../../styles";
import Button from "../button";
import { Modal, Upload } from "antd";
import BackIcon from "../Icons/backIcon";
import { FiDownload } from "react-icons/fi";
import ForwardIcon from "../Icons/forwardIcon";
import { RiErrorWarningFill } from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { CloudUploadOutlined, FileExcelOutlined } from "@ant-design/icons";

export function UploadSpreadSheet({ closeUpload }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [fileData, setFileData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  const intl = useIntl();
  const dispatch = useDispatch();
  const { addToast } = useToast();
  const { token } = useSelector((state) => state.auth);
  const { errorsData, data } = useSelector((state) => state.defaulters);

  const handleErros = useCallback(
    (data) => {
      const { code } = data;
      switch (code) {
        case -43_000:
          const description =
            data?.errors?.length > 0
              ? intl.formatMessage(
                  { id: "defaulters.none_imported" },
                  { rows: data?.errors?.length ?? 0 }
                )
              : intl.formatMessage({ id: "defaulters.upload-empty" });
          addToast({
            title: intl.formatMessage({ id: "error" }),
            description,
            type: "error",
          });
          break;

        case -43_002:
          addToast({
            title: intl.formatMessage({ id: "error" }),
            description: intl.formatMessage({
              id: "defaulter.load-not-possible",
            }),
            type: "error",
          });
          break;

        case -43_004:
          const cols = data?.columns
            ? data?.columns.map((id) => `"${intl.formatMessage({ id })}"`).join(", ")
            : "";

          addToast({
            title: intl.formatMessage({ id: "error" }),
            description: intl.formatMessage(
              { id: "defaulter.error-cols" },
              {
                cols,
              }
            ),
            type: "error",
          });
          break;

        case -43_005:
          addToast({
            title: intl.formatMessage({ id: "error" }),
            description: intl.formatMessage(
              { id: "defaulter.max-rows" },
              { rows: totalRows - 500 }
            ),
            type: "error",
          });
          break;

        default:
          addToast({
            title: intl.formatMessage({
              id: "defaulters.upload-error-internal",
            }),
            type: "error",
          });
          break;
      }
    },
    [addToast, intl, totalRows]
  );

  const handleUpload = useCallback(async () => {
    if (fileData.length === 0) {
      return;
    }

    setOpen(false);

    try {
      setUploading(true);
      dispatch(defaultersResetErrorsData());
      const response = await api.post(
        "/accounts-receivable/debtors/import",
        convertFileToFormData(fileData[0]),
        {
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { data } = response;

      if (data.code === -43_001) {
        addToast({
          title: intl.formatMessage({ id: "warning" }),
          description: intl.formatMessage(
            { id: "defaulters.partial_imported" },
            { rows: data.errors.length, totalRows }
          ),
          type: "info",
        });

        dispatch(
          defaultersErrorsData({
            errors: data.errors,
          })
        );
      }

      dispatch(getDefaultersData());

      if (data.code === 43_000) {
        addToast({
          title: intl.formatMessage({ id: "success" }),
          description: intl.formatMessage({ id: "defaulters.upload_success" }),
          type: "success",
        });
        setTimeout(() => {
          setFileData([]);
        }, 500);

        closeUpload();
      }
    } catch (error) {
      const { response } = error;

      if (response?.data) {
        const { data } = response;
        return handleErros(data);
      }

      return addToast({
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "defaulters.upload-error-internal",
        }),
        type: "error",
      });
    } finally {
      setUploading(false);
    }
  }, [
    addToast,
    closeUpload,
    dispatch,
    fileData,
    handleErros,
    intl,
    token,
    totalRows,
  ]);

  const submit = () => {
    if (data.length > 0) {
      return setOpen(true);
    }

    return handleUpload();
  };

  const parseToCVS = useCallback(
    ({ errorsData }) => {
      if (errorsData.length === 0) return;
      const cleaned = errorsData.map(({ id, ...rest }) => {
        return {
          ...rest,
        };
      });

      const columns = [
        intl.formatMessage({ id: "defaulters.table.sales_person" }),
        intl.formatMessage({ id: "defaulters.table.analysis_date" }),
        intl.formatMessage({ id: "defaulters.table.transaction_currency" }),
        intl.formatMessage({ id: "defaulters.table.customer_name" }),
        intl.formatMessage({ id: "defaulters.table.transaction_number" }),
        intl.formatMessage({ id: "defaulters.table.total" }),
        intl.formatMessage({ id: "defaulters.table.due" }),
        intl.formatMessage({ id: "defaulters.table.overdue" }),
        intl.formatMessage({ id: "defaulters.table.paid" }),
        intl.formatMessage({ id: "defaulters.table.opportunity" }),
        intl.formatMessage({ id: "error" }),
      ];

      const cols = [[...columns]];

      const worksheet = XLSX.utils.json_to_sheet(cleaned);
      XLSX.utils.sheet_add_aoa(worksheet, cols, { origin: "A1" });

      const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
      const csvBlobData = new Blob([String.fromCharCode(0xfeff), csvOutput], {
        type: "text/csv;charset=utf-8",
      });
      FileSaver.saveAs(csvBlobData, `${fileData[0].name}.csv`);
    },
    [fileData, intl]
  );

  const handleDownloadErrosData = useCallback(async () => {
    try {
      if (loading) return;
      setLoading(true);

      parseToCVS({ errorsData });
    } catch (error) {
      addToast({
        type: "error",
        title: intl.formatMessage({
          id: "defaulters.download_failed",
        }),
      });
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    }
  }, [addToast, errorsData, intl, loading, parseToCVS]);

  return (
    <div className="up">
      <UploaderContainer uploading={uploading}>
        <SpreadsheetUploader
          disabled={fileData.length > 0}
          error={error}
          setError={setError}
          setFileData={setFileData}
          setTotalRows={setTotalRows}
        />

        {fileData.length > 0 && (
          <SpreadsheetCard
            file={fileData[0]}
            setFileData={setFileData}
            uploading={uploading}
          />
        )}
      </UploaderContainer>
      {error && <ErrorMessage error={error} />}

      <FlexContainer width={"fit-content"} gap="8px" margin="8px 0 0 auto">
        {errorsData.length > 0 && fileData.length > 0 && (
          <Button
            Icon={
              loading ? <AiOutlineLoading3Quarters /> : <FiDownload size={18} />
            }
            loading={loading}
            disabled={loading}
            variant="destructive"
            label={"defaulters.download"}
            shadow
            onClick={handleDownloadErrosData}
          />
        )}
        <Button
          label={uploading ? "defaulter.uploading" : "send"}
          loading={uploading}
          disabled={uploading || fileData.length === 0}
          Icon={uploading ? <AiOutlineLoading3Quarters /> : <ForwardIcon />}
          onClick={submit}
          shadow
        />
      </FlexContainer>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        centered={true}
        closable={false}
        width={600}
        footer={""}
        className="modalLesson"
        destroyOnClose
      >
        <ModalTitle>
          {intl.formatMessage({
            id: "overwrite-database-warning",
          })}
        </ModalTitle>

        <ModalButtonsWrapper>
          <Button
            label={"goback"}
            variant={"secondary"}
            Icon={<BackIcon size={16} />}
            onClick={() => setOpen(false)}
          />
          <Button
            label={"confirm"}
            Icon={<ForwardIcon />}
            onClick={handleUpload}
          />
        </ModalButtonsWrapper>
      </Modal>
    </div>
  );
}

function SpreadsheetUploader({
  disabled,
  error,
  setTotalRows,
  setError,
  setFileData,
}) {
  const dispatch = useDispatch();

  const props = useMemo(() => {
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    return {
      name: "file",
      showUploadList: false,
      multiple: false,
      accept: ".xlsx, .xls",
      maxCount: 1,
      beforeUpload: (file) => {
        setTotalRows(0);
        dispatch(defaultersResetErrorsData());
        setError(null);
        const isValidType = validTypes.includes(file.type);
        if (!isValidType) {
          setError("defaulters.upload-error-valid-type");

          return Upload.LIST_IGNORE;
        }
        const isLessThan10MB = file.size / 1024 / 1024 < 10;

        if (!isLessThan10MB) {
          setError("defaulters.upload-error-valid-size");
          return Upload.LIST_IGNORE;
        }

        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            if (jsonData.length === 0) {
              setError("defaulters.upload-empty");

              return resolve(Upload.LIST_IGNORE);
            }

            if (jsonData.length > 500) {
              setError("defaulters.upload-error-rows-size");

              resolve(Upload.LIST_IGNORE);
            } else {
              setTotalRows(jsonData.length);
              resolve(false);
            }
          };
          reader.readAsArrayBuffer(file);
        });
      },
      onChange: ({ fileList }) => {
        return fileList.length > 0 ? setFileData(fileList) : setFileData([]);
      },
      onDrop(e) {
        const file = e.dataTransfer.files[0];

        const isValidType = validTypes.includes(file.type);
        if (!isValidType) {
          setError("defaulters.upload-error-valid-type");
          return Upload.LIST_IGNORE;
        }
        const isLessThan10MB = file.size / 1024 / 1024 < 10;

        if (!isLessThan10MB) {
          setError("defaulters.upload-error-valid-size");
          return Upload.LIST_IGNORE;
        }

        if (file) {
          setFileData(file);
        }
      },
    };
  }, [dispatch, setError, setFileData, setTotalRows]);

  return (
    <DnDUploader {...props} disabled={disabled} error={error}>
      <p className="ant-upload-drag-icon">
        <CloudUploadOutlined
          style={{
            color: "#002244",
          }}
        />
      </p>
      <p className="ant-upload-text">
        <FormattedMessage id="defaulters.drop-file-here" />
      </p>
      <p className="ant-upload-hint">
        <FormattedMessage id="defaulters.select-from-computer" />
      </p>
    </DnDUploader>
  );
}

function SpreadsheetCard({ setFileData, file, uploading }) {
  const { name, size } = file;
  return (
    <div className="uploadcard">
      <div className="uploadcardcontainer">
        <FileExcelOutlined />
        <div>
          <p>{name}</p>
          <p className="size">{formatFileSize(size)}</p>
          <p
            className="remover"
            onClick={() => {
              if (uploading) return;
              setFileData([]);
            }}
          >
            Remover
          </p>
        </div>
      </div>
    </div>
  );
}

function ErrorMessage({ error }) {
  return (
    <Warning marginTop="4px" error gap="4px">
      <RiErrorWarningFill />
      <span>
        <FormattedMessage id={error} />
      </span>
    </Warning>
  );
}
